import { z } from 'zod';
import { TimestampSchema } from './common';

/** Home sizes (Israeli room-count convention). */
export const HOME_SIZES = ['studio', 'rooms_2', 'rooms_3', 'rooms_4', 'rooms_5plus'] as const;
export type HomeSize = (typeof HOME_SIZES)[number];

export const MOVE_TYPES = ['local', 'long_distance'] as const;
export type MoveType = (typeof MOVE_TYPES)[number];

/** Move scope: the size of what's moving on each end. */
export const MOVE_SCOPES = ['room', 'home'] as const;
export type MoveScope = (typeof MOVE_SCOPES)[number];

/** The four named scenarios, derived from the origin×destination pair. */
export type MoveScenario = 'home_to_home' | 'room_to_home' | 'room_to_room' | 'home_to_room';

/**
 * Derive the named scenario from the two axes. Unset axes default to 'home',
 * so a legacy profile (no scope fields) yields 'home_to_home' — today's behavior.
 * Pure → unit tested.
 */
export function moveScenario(origin?: MoveScope, destination?: MoveScope): MoveScenario {
  const o: MoveScope = origin ?? 'home';
  const d: MoveScope = destination ?? 'home';
  return `${o}_to_${d}` as MoveScenario;
}

/**
 * users/{uid}/meta/moveProfile — the move setup (V1.5). Powers the readiness
 * score and, later, the mover-lead package. All fields optional (wizard can be
 * partially filled / skipped).
 */
export const MoveProfileSchema = z.object({
  moveDate: TimestampSchema.optional(),
  /** Destination address (Settle-In phase 1) — prefills leads + the gov.il link. */
  newAddress: z.string().trim().max(200).optional(),
  currentHomeSize: z.enum(HOME_SIZES).optional(),
  destinationHomeSize: z.enum(HOME_SIZES).optional(),
  moveType: z.enum(MOVE_TYPES).optional(),
  /** Origin scope — drives cataloging/rooms/readiness. */
  originScope: z.enum(MOVE_SCOPES).optional(),
  /** Destination scope — drives Settle-In bureaucracy. */
  destinationScope: z.enum(MOVE_SCOPES).optional(),
  /** Set only when originScope === 'room': the single room being moved. */
  sourceRoom: z.string().trim().max(60).optional(),
  updatedAt: TimestampSchema.optional(),
});
export type MoveProfile = z.infer<typeof MoveProfileSchema>;

/** Editable fields (everything except the server-managed timestamp). */
export type MoveProfileDraft = Omit<MoveProfile, 'updatedAt'>;
